import { useEffect, useState } from 'react';
import * as R from 'ramda';
import { stopNavigationEventsPropagation } from 'yet-another-react-lightbox';

import { Icon, IconButton } from '@atom/mui';
import { CloseIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { Media } from '@atom/types/media';
import { mediaFileName } from '@atom/utilities/mediaUtilities';
import textDisplayUtilities from '@atom/utilities/textDisplayUtilities';
import { getUtcDisplayDate } from '@atom/utilities/timeUtilities';

import MediaLightboxCapturedDateField from './MediaLightboxCapturedDateField';
import MediaLightboxDescriptionField from './MediaLightboxDescriptionField';
import MediaLightboxDetailRow from './MediaLightboxDetailRow';

import classes from './mediaLightbox.module.css';

export type MediaLightboxDetailsProps = {
  media: Media;
  canUpdateMedia: boolean;
  onClose: () => void;
  onRefetch?: () => void;
};

const MediaLightboxDetails = ({
  media,
  canUpdateMedia,
  onClose,
  onRefetch = () => {},
}: MediaLightboxDetailsProps) => {
  const [editingField, setEditingField] = useState<
    'capturedDate' | 'description'
  >(null);

  useEffect(() => {
    setEditingField(null);
  }, [media.id]);

  return (
    <div
      className={classes.details}
      data-cy="mediaLightboxDetails"
      {...stopNavigationEventsPropagation()}
    >
      <div className={classes.detailsHeader}>
        <div className={classes.detailsTitle}>Details</div>
        <IconButton
          size="small"
          tooltip="Close"
          onClick={onClose}
          data-cy="mediaLightboxDetailsCloseButton"
        >
          <Icon color={colors.neutral.white} icon={CloseIcon} />
        </IconButton>
      </div>
      <div className={classes.detailsContent}>
        <MediaLightboxDetailRow label="Name">
          {mediaFileName(media)}
        </MediaLightboxDetailRow>
        <MediaLightboxDetailRow label="Uploaded by">
          {!R.isEmpty(media.createdBy)
            ? textDisplayUtilities.displayName(media.createdBy)
            : ''}
        </MediaLightboxDetailRow>
        <MediaLightboxDetailRow label="Uploaded on">
          {getUtcDisplayDate(media.createdDate)}
        </MediaLightboxDetailRow>
        <MediaLightboxCapturedDateField
          media={media}
          editable={canUpdateMedia}
          editing={editingField === 'capturedDate'}
          onEdit={() => setEditingField('capturedDate')}
          onDone={() => setEditingField(null)}
          onSaved={onRefetch}
        />
        <MediaLightboxDescriptionField
          media={media}
          editable={canUpdateMedia}
          editing={editingField === 'description'}
          onEdit={() => setEditingField('description')}
          onDone={() => setEditingField(null)}
          onSaved={onRefetch}
        />
      </div>
    </div>
  );
};

export default MediaLightboxDetails;
